/* @flow */
import URI from 'urijs';
import * as React from 'react';
import { Route } from 'react-router';

import MessagePageLayout from 'messages/components/MessagePageLayout';
import ChatMessageContainer from 'messages/containers/ChatMessageContainer';

import type { RouterState, LocationDescriptor } from 'lib/ReactRouterTypes';
import EventTracking from 'lib/EventTracking';
import { EventTrackingConstants } from 'constants/EventTrackingConstants';

function hasHTTPProtocol(url: string) {
  if (!url) {
    return false;
  }

  const uri = URI(url);
  const protocol = uri.protocol();
  return protocol === 'http' || protocol === 'https';
}

/**
 * Backwards compatibility with urls like /messages/b3996670-890d-4d75-a5f3-02948fe02145?redirect=aHR0cDovL3d3dy50aGVvbmlvbi5jb20%3D
 * In r101-postoffice we used to transform links sent to SMS devices to proxy them through the message viewer
 */
function honorRedirectParam(
  nextState: RouterState,
  replaceWith: (location: LocationDescriptor) => void
) {
  const redirectB64 = nextState.location.query.redirect;
  if (redirectB64) {
    const url: string = Buffer.from(redirectB64, 'base64').toString();
    const redirectTo = hasHTTPProtocol(url) ? url : '/send_the_app';

    if (typeof window === 'undefined') {
      // React Router won't transitionTo full urls, but we'll cheat and use
      // this callback to pass this url to the match() handler running in our
      // Express server.
      replaceWith({ pathname: redirectTo });
    } else {
      window.location.replace(redirectTo);
    }
  }
}

/**
 * We no longer support /messages/:uuid URLs since https://app.asana.com/0/52068106376728/228507512461672
 *
 * The <Redirect> directive only does local but we don't want to include those views in this build.
 */
function hardRedirectLegacyMessages(
  nextState: RouterState,
  replaceWith: (location: LocationDescriptor) => void
) {
  const url = '/send_the_app';
  if (typeof window === 'undefined') {
    replaceWith({ pathname: url });
  } else {
    window.location.replace(url);
  }
}

function trackChatMessageRequest(nextState: RouterState) {
  const { _t: recipientUuid } = nextState.location.query;
  if (recipientUuid) {
    EventTracking.request(EventTrackingConstants.TEXT_MESSAGE, {
      uuid: recipientUuid,
    });
  }
}

// Urls for manual testing:
// image chat message http://www.remind.localhost/messages/chats/bfd3e958-e7da-4d1c-b23c-2e35b5793da0/d7205628-a746-4846-9e41-f88488f68c10?_t=07ac49a2-6e62-4dbe-a305-6241e2b7803b#
// audio announcement message http://www.remind.localhost/messages/bd27dc61-5706-4d8e-9e34-b4a3fd1711de?_t=1deffe34-5909-40cc-b9cc-85ba6e339f6c
// video announcement http://www.remind.localhost/messages/6af5dea2-ca6e-4634-ae14-76e38486b517

export default (
  <Route component={MessagePageLayout} onEnter={honorRedirectParam}>
    <Route
      path="/messages/chats/:chatThreadUuid/:chatMessageUuid"
      component={ChatMessageContainer}
      onEnter={trackChatMessageRequest}
    />
    <Route path="/messages/:messageUuid" onEnter={hardRedirectLegacyMessages} />
  </Route>
);
